import { extractRecordPk, type RecordWithMeta } from "../api/recordParsers";

/**
 * Fields that identify the same real-world record for a resource when the
 * search index / flavour-loader returns it more than once under different
 * server PKs (common after a local re-seed).
 *
 * Order is preference, not precedence: a row is dropped when ANY of these
 * fields repeats a value already kept. A re-seed does not always carry the
 * business number over, so matching on the number alone left the second copy
 * on the Partners list; the display name is what a user actually sees twice.
 */
const BUSINESS_KEYS_BY_RESOURCE: Readonly<Record<string, readonly string[]>> = {
  Supplier: ["supplierNumber", "name"],
};

/** Case- and whitespace-insensitive, so "Albini  Group" and "albini group" match. */
const normalizeKey = (raw: unknown): string =>
  typeof raw === "string" ? raw.trim().replace(/\s+/g, " ").toLowerCase() : "";

/**
 * First-wins dedupe for collection query results.
 *
 * Always collapses identical primary keys. For known party resources, also
 * collapses on the business keys above, so a double-seeded Albini (two `DATA_*`
 * ids) only appears once in the Partners / Supplier table.
 *
 * The trade-off is deliberate: two genuinely distinct partners that share a
 * name collapse to one row. On this data that is the far rarer case, and the
 * duplicate rows are a seeding artefact — the real fix is cleaning the source
 * records, not this pass.
 */
export const dedupeCollectionRecords = (
  resource: string,
  records: readonly RecordWithMeta[],
): RecordWithMeta[] => {
  const businessFields = BUSINESS_KEYS_BY_RESOURCE[resource] ?? [];
  const seenPk = new Set<string>();
  const seenByField = new Map<string, Set<string>>();
  const out: RecordWithMeta[] = [];

  for (const record of records) {
    const pk = extractRecordPk(record) ?? extractRecordPk(record.data);
    if (pk && seenPk.has(pk)) {
      continue;
    }

    const claims: Array<[string, string]> = [];
    let duplicate = false;
    for (const field of businessFields) {
      const value = normalizeKey(record.data[field]);
      if (!value) {
        continue;
      }
      if (seenByField.get(field)?.has(value)) {
        duplicate = true;
        break;
      }
      claims.push([field, value]);
    }
    if (duplicate) {
      continue;
    }

    if (pk) {
      seenPk.add(pk);
    }
    for (const [field, value] of claims) {
      const seen = seenByField.get(field) ?? new Set<string>();
      seen.add(value);
      seenByField.set(field, seen);
    }
    out.push(record);
  }

  return out;
};

/**
 * Drop repeats of the same primary key, and nothing else.
 *
 * For server-paged reads. A business-key collapse cannot be made consistent with
 * server paging: each page shrinks by however many copies it happens to hold,
 * so the grid showed 7 rows under a 25-row page size and a total that changed
 * from page to page. A page shows what the server paged; duplicate source
 * records are a data fix.
 */
export const dedupeRecordsByPk = (records: readonly RecordWithMeta[]): RecordWithMeta[] => {
  const seenPk = new Set<string>();

  return records.filter((record) => {
    const pk = extractRecordPk(record) ?? extractRecordPk(record.data);
    if (!pk) {
      return true;
    }
    if (seenPk.has(pk)) {
      return false;
    }
    seenPk.add(pk);
    return true;
  });
};
